import { useState } from 'react'
import { X, AlertCircle, CheckCircle, Loader, Lock, ExternalLink } from 'lucide-react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { usersAPI } from '../services/api'

const RowField = ({ label, required, children }) => (
  <div className="flex min-h-[34px] border-b border-gray-100 last:border-0">
    <div className="w-36 shrink-0 bg-gray-50 border-r border-gray-100 px-2 py-1.5 flex items-center text-[11px] text-gray-500 font-medium leading-tight">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    <div className="flex-1 px-2 py-1 flex items-center min-w-0">{children}</div>
  </div>
)

const TextInput = ({ value, onChange, placeholder, type = 'text', readOnly, className = '' }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
    className={`w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 bg-white ${readOnly ? 'bg-gray-50 text-gray-500' : ''} ${className}`}
  />
)

const CheckboxInput = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-1.5 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} className="w-3.5 h-3.5 accent-blue-600" />
    {label && <span className="text-xs text-gray-600">{label}</span>}
  </label>
)

const SelectInput = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={onChange}
    className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-400 bg-white"
  >
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
)

const TwoColRow = ({ left, right }) => (
  <div className="flex border-b border-gray-100 last:border-0">
    {/* Left pair */}
    <div className="flex flex-1 border-r border-gray-100 min-w-0">
      <div className="w-36 shrink-0 bg-gray-50 border-r border-gray-100 px-2 py-1.5 flex items-center text-[11px] text-gray-500 font-medium leading-tight">
        {left.label}{left.required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      <div className="flex-1 px-2 py-1 flex items-center min-w-0">{left.content}</div>
    </div>
    {/* Right pair */}
    {right && (
      <div className="flex flex-1 min-w-0">
        <div className="w-36 shrink-0 bg-gray-50 border-r border-gray-100 px-2 py-1.5 flex items-center text-[11px] text-gray-500 font-medium leading-tight">
          {right.label}{right.required && <span className="text-red-500 ml-0.5">*</span>}
        </div>
        <div className="flex-1 px-2 py-1 flex items-center min-w-0">{right.content}</div>
      </div>
    )}
  </div>
)

const NOTIFICATION_OPTIONS = [
  { value: 'Enable', label: 'Enable' },
  { value: 'Disable', label: 'Disable' },
]

const CALENDAR_OPTIONS = [
  { value: 'Outlook', label: 'Outlook' },
  { value: 'Google', label: 'Google' },
  { value: 'None', label: 'None' },
]

const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
]

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT)' },
]

const TYPE_OPTIONS = [
  { value: 'Client', label: 'Client' },
  { value: 'Vendor', label: 'Vendor' },
]

const PORTAL_OPTIONS = [
  { value: 'Client Portal', label: 'Client Portal' },
  { value: 'Vendor Portal', label: 'Vendor Portal' },
  { value: 'Both', label: 'Both Portals' },
]

export default function UserDetailPanel({ isOpen, onClose, onSuccess }) {
  const queryClient = useQueryClient()

  const [errors, setErrors] = useState({})
  const [form, setForm] = useState({
    saml_user: '',
    active: true,
    db_directory_id: '',
    web_service_access: false,
    user_id: '',
    password: '',
    password_history: '',
    first_name: '',
    email: '',
    name_display: '',
    notification: 'Enable',
    preferred_first_name: '',
    calendar_integration: 'Outlook',
    last_name: '',
    phone: '',
    date_format: 'DD/MM/YYYY',
    middle_name: '',
    time_zone: 'Asia/Kolkata',
    title: '',
    vip: false,
    coast_service_level: '',
    vid: '',
    manager: '',
    floor: '',
    cost_center: '',
    street: '',
    group_division: '',
    city: '',
    corporate_division: '',
    state_province: '',
    business_division: '',
    zip_postal_code: '',
    country_code: '',
    user_type: 'Client',
    portal_access: 'Client Portal',
  })

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm(f => ({ ...f, [field]: val }))
    if (errors[field]) setErrors(er => ({ ...er, [field]: undefined }))
  }

  const validate = () => {
    const e = {}
    if (!form.first_name.trim()) e.first_name = 'Required'
    if (!form.last_name.trim()) e.last_name = 'Required'
    if (!form.user_id.trim()) e.user_id = 'Required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.user_id)) e.user_id = 'Must be a valid email'
    if (!form.password) e.password = 'Required'
    else if (form.password.length < 8) e.password = 'Min 8 characters'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const createUser = useMutation({
    mutationFn: (payload) => usersAPI.create(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onSuccess?.(res?.data?.data)
      handleClose()
    }
  })

  const handleClose = () => {
    setErrors({})
    setForm({
      saml_user: '', active: true, db_directory_id: '', web_service_access: false,
      user_id: '', password: '', password_history: '', first_name: '', email: '',
      name_display: '', notification: 'Enable', preferred_first_name: '', calendar_integration: 'Outlook',
      last_name: '', phone: '', date_format: 'DD/MM/YYYY', middle_name: '', time_zone: 'Asia/Kolkata',
      title: '', vip: false, coast_service_level: '', vid: '', manager: '', floor: '',
      cost_center: '', street: '', group_division: '', city: '', corporate_division: '',
      state_province: '', business_division: '', zip_postal_code: '', country_code: '',
      user_type: 'Client', portal_access: 'Client Portal',
    })
    onClose()
  }

  const handleSubmit = () => {
    if (!validate()) return
    createUser.mutate({
      name: `${form.first_name.trim()} ${form.last_name.trim()}`,
      email: form.user_id.trim().toLowerCase(),
      phone: form.phone.trim() || null,
      password: form.password,
      type: form.user_type,
      portal_access: form.portal_access,
      status: form.active ? 'Active' : 'Inactive',
    })
  }

  if (!isOpen) return null

  const F = form

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40" onClick={handleClose} />

      {/* Panel */}
      <div className="w-[680px] max-w-full bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#1e293b] text-white shrink-0">
          <span className="font-semibold text-sm">User</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold bg-white text-[#1e293b] rounded border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Open Record
            </button>
            <button onClick={handleClose} className="p-1 rounded hover:bg-white/10 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Section: Identity */}
          <div className="border-b border-gray-200">
            <div className="bg-[#f0f4f8] px-3 py-1 border-b border-gray-200">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">User Details</span>
            </div>

            <div className="divide-y divide-gray-100">
              <TwoColRow
                left={{
                  label: 'SAML User',
                  content: <TextInput value={F.saml_user} onChange={set('saml_user')} />
                }}
                right={{
                  label: 'Active',
                  content: <CheckboxInput checked={F.active} onChange={set('active')} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'db Directory ID',
                  content: <TextInput value={F.db_directory_id} onChange={set('db_directory_id')} />
                }}
                right={{
                  label: 'Web Service Access Only',
                  content: <CheckboxInput checked={F.web_service_access} onChange={set('web_service_access')} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'User ID',
                  required: true,
                  content: (
                    <div className="w-full">
                      <TextInput
                        value={F.user_id}
                        onChange={set('user_id')}
                        placeholder="user@company.com"
                        className={errors.user_id ? 'border-red-400' : ''}
                      />
                      {errors.user_id && <p className="text-[10px] text-red-500 mt-0.5">{errors.user_id}</p>}
                    </div>
                  )
                }}
                right={{
                  label: 'Password',
                  required: true,
                  content: (
                    <div className="w-full">
                      <TextInput
                        type="password"
                        value={F.password}
                        onChange={set('password')}
                        placeholder="Min 8 characters"
                        className={errors.password ? 'border-red-400' : ''}
                      />
                      {errors.password && <p className="text-[10px] text-red-500 mt-0.5">{errors.password}</p>}
                    </div>
                  )
                }}
              />
              <TwoColRow
                left={{
                  label: 'First name',
                  required: true,
                  content: (
                    <div className="w-full">
                      <TextInput
                        value={F.first_name}
                        onChange={set('first_name')}
                        className={errors.first_name ? 'border-red-400' : ''}
                      />
                      {errors.first_name && <p className="text-[10px] text-red-500 mt-0.5">{errors.first_name}</p>}
                    </div>
                  )
                }}
                right={{
                  label: 'Email',
                  content: <TextInput value={F.email} onChange={set('email')} placeholder="Alternate email" />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Name',
                  content: (
                    <TextInput
                      value={F.first_name || F.last_name ? `${F.first_name} ${F.middle_name ? F.middle_name + ' ' : ''}${F.last_name}`.trim() : ''}
                      readOnly
                      placeholder="Auto-generated"
                    />
                  )
                }}
                right={{
                  label: 'Notification',
                  content: <SelectInput value={F.notification} onChange={set('notification')} options={NOTIFICATION_OPTIONS} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Preferred First Name',
                  content: <TextInput value={F.preferred_first_name} onChange={set('preferred_first_name')} />
                }}
                right={{
                  label: 'Calendar Integration',
                  content: <SelectInput value={F.calendar_integration} onChange={set('calendar_integration')} options={CALENDAR_OPTIONS} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Phone',
                  content: <TextInput value={F.phone} onChange={set('phone')} placeholder="+91 9876543210" />
                }}
                right={{
                  label: '',
                  content: null
                }}
              />
              <TwoColRow
                left={{
                  label: 'Last name',
                  required: true,
                  content: (
                    <div className="w-full">
                      <TextInput
                        value={F.last_name}
                        onChange={set('last_name')}
                        className={errors.last_name ? 'border-red-400' : ''}
                      />
                      {errors.last_name && <p className="text-[10px] text-red-500 mt-0.5">{errors.last_name}</p>}
                    </div>
                  )
                }}
                right={{
                  label: 'Date format',
                  content: <SelectInput value={F.date_format} onChange={set('date_format')} options={DATE_FORMAT_OPTIONS} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Middle name',
                  content: <TextInput value={F.middle_name} onChange={set('middle_name')} />
                }}
                right={{
                  label: 'Time zone',
                  content: <SelectInput value={F.time_zone} onChange={set('time_zone')} options={TIMEZONE_OPTIONS} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Title',
                  content: <TextInput value={F.title} onChange={set('title')} placeholder="e.g. Senior Engineer" />
                }}
                right={{
                  label: 'VIP',
                  content: <CheckboxInput checked={F.vip} onChange={set('vip')} />
                }}
              />
            </div>
          </div>

          {/* Section: Organization */}
          <div className="border-b border-gray-200">
            <div className="bg-[#f0f4f8] px-3 py-1 border-b border-gray-200">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Organization</span>
            </div>
            <div className="divide-y divide-gray-100">
              <TwoColRow
                left={{
                  label: 'Coast Service Level',
                  content: <TextInput value={F.coast_service_level} onChange={set('coast_service_level')} />
                }}
                right={{
                  label: 'VID',
                  content: <TextInput value={F.vid} onChange={set('vid')} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Manager',
                  content: <TextInput value={F.manager} onChange={set('manager')} />
                }}
                right={{
                  label: 'Floor',
                  content: <TextInput value={F.floor} onChange={set('floor')} placeholder="e.g. First" />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Cost center',
                  content: <TextInput value={F.cost_center} onChange={set('cost_center')} />
                }}
                right={{
                  label: 'Street',
                  content: <TextInput value={F.street} onChange={set('street')} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Group Division',
                  content: <TextInput value={F.group_division} onChange={set('group_division')} />
                }}
                right={{
                  label: 'City',
                  content: <TextInput value={F.city} onChange={set('city')} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Corporate Division',
                  content: <TextInput value={F.corporate_division} onChange={set('corporate_division')} />
                }}
                right={{
                  label: 'State / Province',
                  content: <TextInput value={F.state_province} onChange={set('state_province')} />
                }}
              />
              <TwoColRow
                left={{
                  label: 'Business Division',
                  content: <TextInput value={F.business_division} onChange={set('business_division')} />
                }}
                right={{
                  label: 'Zip / Postal code',
                  content: <TextInput value={F.zip_postal_code} onChange={set('zip_postal_code')} />
                }}
              />
              <TwoColRow
                left={{ label: '', content: null }}
                right={{
                  label: 'Country code',
                  content: <TextInput value={F.country_code} onChange={set('country_code')} placeholder="e.g. IN, US" />
                }}
              />
            </div>
          </div>

          {/* Section: Access */}
          <div>
            <div className="bg-[#f0f4f8] px-3 py-1 border-b border-gray-200">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Platform Access</span>
            </div>
            <div className="divide-y divide-gray-100">
              <TwoColRow
                left={{
                  label: 'User Type',
                  content: <SelectInput value={F.user_type} onChange={set('user_type')} options={TYPE_OPTIONS} />
                }}
                right={{
                  label: 'Portal Access',
                  content: <SelectInput value={F.portal_access} onChange={set('portal_access')} options={PORTAL_OPTIONS} />
                }}
              />
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-200 px-4 py-3 bg-gray-50 flex items-center justify-between gap-3">
          {createUser.isError && (
            <div className="flex items-center gap-1.5 text-xs text-red-600">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {createUser.error?.response?.data?.message || 'Failed to create user'}
            </div>
          )}
          {!createUser.isError && <div />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createUser.isPending}
              className="flex items-center gap-1.5 px-5 py-1.5 text-sm font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              {createUser.isPending
                ? <><Loader className="w-4 h-4 animate-spin" />Creating…</>
                : <><CheckCircle className="w-4 h-4" />Create User</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
